import { useState } from "react";
import { ArrowRight, Search, X } from "lucide-react";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { articles, resourceCategories, resourcePath } from "@/data/resources";
import { seoForPath, useSeo } from "@/lib/seo";

const ResourcesIndex = () => {
  useSeo(seoForPath("/resources/"));
  const [active, setActive] = useState<string>("All");
  const [query, setQuery] = useState("");
  const filters = ["All", ...resourceCategories.map((c) => c.name)];
  const q = query.trim().toLowerCase();
  const matches = (a: (typeof articles)[number]) =>
    !q || `${a.title} ${a.metaDescription} ${a.intro} ${a.category}`.toLowerCase().includes(q);
  const visibleCats = resourceCategories
    .filter((c) => active === "All" || c.name === active)
    .map((c) => ({ cat: c, list: articles.filter((a) => a.category === c.name && matches(a)) }))
    .filter((x) => x.list.length > 0);
  const total = visibleCats.reduce((n, x) => n + x.list.length, 0);
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />
      <main>
        <section className="pt-28 pb-12 md:pt-36 md:pb-16 bg-hero-network">
          <div className="container max-w-4xl">
            <p className="font-mono text-xs uppercase tracking-widest text-primary">Resources</p>
            <h1 className="mt-4 font-display text-4xl md:text-5xl font-bold tracking-tight">
              Engineering Guides for Enterprise Systems
            </h1>
            <p className="mt-5 text-lg text-muted-foreground leading-relaxed max-w-3xl">
              Practical explanations of enterprise integration, change data capture, observability, enterprise architecture, AI-driven operational intelligence and integration delivery management; written for engineers and architects working on complex, distributed environments.
            </p>
          </div>
        </section>

        <div className="container max-w-5xl pt-8 space-y-5">
          <div className="relative max-w-xl">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search guides…"
              aria-label="Search guides"
              className="w-full rounded-full border border-border bg-card py-2.5 pl-11 pr-10 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
            />
            {query && (
              <button type="button" onClick={() => setQuery("")} aria-label="Clear search" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <div role="group" aria-label="Filter guides by category" className="flex flex-wrap gap-2">
            {filters.map((f) => (
              <button
                key={f}
                type="button"
                aria-pressed={active === f}
                onClick={() => setActive(f)}
                className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${
                  active === f
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-card text-muted-foreground hover:border-primary/40 hover:text-foreground"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
          {q && <p className="text-sm text-muted-foreground" aria-live="polite">{total} {total === 1 ? "guide" : "guides"} found</p>}
        </div>

        <div className="container max-w-5xl pt-10 pb-24 space-y-16">
          {visibleCats.length === 0 && (
            <div className="rounded-2xl border border-border bg-card p-10 text-center">
              <p className="text-muted-foreground">No guides match your search.</p>
              <button type="button" onClick={() => { setQuery(""); setActive("All"); }} className="mt-4 text-sm font-medium text-primary">
                Reset filters
              </button>
            </div>
          )}
          {visibleCats.map(({ cat, list }) => {
            return (
              <section key={cat.name} aria-labelledby={`cat-${cat.name}`}>
                <div className="border-b border-border pb-4 mb-6">
                  <h2 id={`cat-${cat.name}`} className="font-display text-2xl font-semibold">{cat.name}</h2>
                  <p className="mt-1 text-sm text-muted-foreground">{cat.description}</p>
                </div>
                <div className="grid gap-5 md:grid-cols-2">
                  {list.map((a) => (
                    <a
                      key={a.slug}
                      href={resourcePath(a.slug)}
                      className="group rounded-2xl border border-border bg-card p-6 transition-all hover:border-primary/40 hover:shadow-lg"
                    >
                      <p className="font-mono text-xs text-muted-foreground">{a.readingMinutes} min read</p>
                      <h3 className="mt-2 font-display text-xl font-semibold group-hover:text-primary transition-colors">{a.title}</h3>
                      <p className="mt-3 text-sm text-muted-foreground leading-relaxed line-clamp-3">{a.metaDescription}</p>
                      <span className="mt-4 inline-flex items-center text-sm font-medium text-primary">
                        Read guide <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                      </span>
                    </a>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ResourcesIndex;
